// quantiix-shell/src/components/ShellBreadcrumbs.jsx
import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Breadcrumb } from '@quantiix/design-system';
import {
    useDataLayer,
    useCachedHierarchy,
    findMachine,
    findOU,
    findFactory
} from '@quantiix/data-layer';
import { ROUTES, parseCurrentRoute } from '../config/routes';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '@/utils/logger.js';

const logger = createLogger('Shell::ShellBreadcrumbs');

function ShellBreadcrumbs() {
    const navigate = useNavigate();
    const location = useLocation();
    const { companyId, navigationSource } = useDataLayer();
    const hierarchy = useCachedHierarchy(companyId);
    const { isSuperuser, hasAssignedCompany, primaryCompany } = useAuth();

    const pathname = location.pathname;

    logger.info('Render', {
        pathname,
        companyId,
        navigationSource,
        hasHierarchy: !!hierarchy
    });

    // Parse route - now includes source
    const { entityType, entityId, source } = useMemo(
        () => parseCurrentRoute(pathname),
        [pathname]
    );

    // Early exit - companies list doesn't need breadcrumbs
    if (entityType === 'companies') {
        return null;
    }

    // Navbar navigation hides breadcrumbs (except for company level)
    // Note: Config MFE doesn't use navbar navigation, so this won't affect it
    if (navigationSource === 'navbar' && entityType !== 'company') {
        return null;
    }

    if (!hierarchy || !entityType) {
        return null;
    }

    // Derive context from hierarchy (unchanged logic)
    const context = (() => {
        switch (entityType) {
            case 'machine':
                return findMachine(hierarchy, entityId);
            case 'ou':
            case 'publicview':
                return findOU(hierarchy, entityId);
            case 'factory':
                return findFactory(hierarchy, entityId);
            case 'company':
                return { company: { id: hierarchy.id, name: hierarchy.name } };
            default:
                return null;
        }
    })();

    if (!context) {
        return null;
    }

    // The "Dashboard" crumb must land where the navbar logo lands: a superuser
    // (or a user with no assigned company) goes to the company list; a regular
    // single-company user goes straight to their company — never the empty
    // superuser company-list view. Mirrors HomePage's redirect logic.
    const dashboardHomeHref = (isSuperuser || !hasAssignedCompany || !primaryCompany?.id)
        ? ROUTES.companies()
        : ROUTES.factories(primaryCompany.id);

    // Build hrefs based on source
    const hrefs = buildHrefs(context, source, dashboardHomeHref);

    logger.info('Built hrefs', {
        source,
        entityType,
        hrefs,
        context: {
            company: context.company?.name,
            factory: context.factory?.name,
            ou: context.ou?.name
        }
    });

    return (
        <Breadcrumb
            companyName={context.company?.name}
            factoryName={context.factory?.name}
            ouName={context.ou?.name}
            machineName={context.machine?.name}
            {...hrefs}
            onNavigate={navigate}
        />
    );
}

/**
 * Build breadcrumb hrefs based on source MFE
 * Extracted to keep main component clean
 */
function buildHrefs(context, source, dashboardHomeHref) {
    // Config MFE uses different routes
    if (source === 'config') {
        return {
            dashboardHref: ROUTES.config.companies(),
            companyHref: context.company
                ? ROUTES.config.factories(context.company.id)
                : undefined,
            factoryHref: context.factory
                ? ROUTES.config.ous(context.factory.id)
                : undefined,
            ouHref: context.ou
                ? ROUTES.config.publicviews(context.ou.id)
                : undefined,
        };
    }

    // Default: Dashboard/PublicView/Machine routes (existing behavior)
    return {
        dashboardHref: dashboardHomeHref,
        companyHref: context.company
            ? ROUTES.factories(context.company.id)
            : undefined,
        factoryHref: context.factory
            ? ROUTES.ous(context.factory.id)
            : undefined,
        ouHref: context.ou
            ? ROUTES.publicview(context.ou.id)
            : undefined,
    };
}

export default ShellBreadcrumbs;